import { useTranslation } from 'react-i18next'
import { AiChatConversationContent } from './ai-chat-conversation-content'
import { AiChatConversationControls } from './ai-chat-conversation-controls'
import { AiChatPanelContext } from './ai-chat-panel-context'
import { AiChatPanelFrame, AiChatPanelPinButton } from './ai-chat-panel-frame'
import { AiChatNavigationContext } from './ai-chat-navigation-context'
import { useAiChatNavigation } from './use-ai-chat-navigation'
import { useAiChatPanelState } from './use-ai-chat-panel-state'
import { useAiChatLiveState } from './ai-chat-live-state'
import { getDrawerCopy } from './ai-chat-live-drawer'
import { documentCollectionPrompts } from './ai-chat-live-content'
import type { ReactNode } from 'react'

type PanelProps = {
  navigationPinned?: boolean
  header?: ReactNode
  pathname?: string
  children: ReactNode
  enabled: boolean
  userId?: string
  workspaceId?: string
  scope?: 'workspace'
}

export function AiChatPanelLayout(props: PanelProps) {
  if (!props.enabled || !props.userId || !props.workspaceId)
    return (
      <>
        {props.header}
        <div className="min-h-0 flex-1">{props.children}</div>
      </>
    )

  return (
    <ScopedAiChatPanelLayout
      key={`${props.userId}:${props.workspaceId}`}
      {...props}
      userId={props.userId}
      workspaceId={props.workspaceId}
    />
  )
}

function ScopedAiChatPanelLayout({
  children,
  header,
  navigationPinned,
  userId,
  workspaceId,
  pathname = '/document-collections',
}: PanelProps & { userId: string; workspaceId: string }) {
  const scope = 'workspace'
  const panel = useAiChatPanelState({ userId, workspaceId, scope })
  const navigation = useAiChatNavigation(pathname)
  const state = useAiChatLiveState({
    navigationContext: navigation.context,
    workspaceId,
    scope,
    open: panel.open,
  })
  const { t } = useTranslation('aiChat')
  const copy = getDrawerCopy({ scope, t })

  return (
    <AiChatPanelContext.Provider
      value={{
        openChat: panel.openChat,
        toggleChat: panel.toggleChat,
        open: panel.open,
      }}
    >
      <AiChatNavigationContext.Provider
        value={{ pathname, setPageContext: navigation.setPageContext }}
      >
        {header}
        <div className="min-h-0 flex-1">
          <AiChatPanelFrame
            panel={panel}
            navigationPinned={navigationPinned}
            title={state.sessionTitle ?? t('newConversation')}
            subtitle={getNavigationSubtitle(navigation, copy.title, t)}
            page={children}
            conversationControls={<PanelConversationControls state={state} />}
          >
            <AiChatConversationContent
              state={state}
              prompts={getNavigationPrompts(navigation, copy.prompts)}
              scope={scope}
              footerAction={<AiChatPanelPinButton panel={panel} />}
            />
          </AiChatPanelFrame>
        </div>
      </AiChatNavigationContext.Provider>
    </AiChatPanelContext.Provider>
  )
}

function getNavigationSubtitle(
  navigation: ReturnType<typeof useAiChatNavigation>,
  fallback: string,
  t: ReturnType<typeof useTranslation<'aiChat'>>['t'],
) {
  if (navigation.label) return navigation.label
  const current = navigation.context.current
  if (current.recordId) return t('navigationRecord', { id: current.recordId })
  if (current.documentCollectionId)
    return t('navigationCollection', { id: current.documentCollectionId })

  return fallback
}

function PanelConversationControls({
  state,
}: {
  state: ReturnType<typeof useAiChatLiveState>
}) {
  const { t } = useTranslation('aiChat')

  return (
    <>
      <p className="text-xs text-muted-foreground">{t('navigationHint')}</p>
      <AiChatConversationControls
        disabled={state.blocked}
        historyOpen={state.historyOpen}
        onNewConversation={state.newConversation}
        onHistory={state.openHistory}
        onBack={state.closeHistory}
      />
    </>
  )
}

function getNavigationPrompts(
  navigation: ReturnType<typeof useAiChatNavigation>,
  workspacePrompts: Array<string>,
) {
  if (navigation.context.current.recordId)
    return [
      'recordPromptSummarize',
      'recordPromptRelated',
      'recordPromptNextSteps',
    ]
  if (navigation.context.current.documentCollectionId)
    return documentCollectionPrompts

  return workspacePrompts
}
